import { IColumn } from "@fluentui/react";
import React, { ReactElement, useEffect, useState } from "react";

import { DateTimeDisplay } from "../../../hooks";
import { ensure, getFileExtension, getFileName } from "../../../utils";
import { ButtonRenderProps } from "../../inputs/button/ButtonRenderProps";
import { CommandBarButton } from "../../inputs/button/CommandBarButton";
import { SearchBox } from "../../inputs/search-box/SearchBox";
import { ContentSection } from "../../items/ContentSection";
import { Icon } from "../../items/Icon";
import { Text } from "../../items/Text";
import { Stack } from "../../layouts/stack/Stack";
import { DetailsRowProps, ListTable, RenderFunction } from "../../lists/ListTable";
import { createListTableColumns, ListTableColumn } from "../../lists/ListTableColumn";
import { Breadcrumb, BreadcrumbItem } from "../../navigations/Breadcrumb";
import { Link } from "../../navigations/Link";
import { Theme } from "../../theme/Theme";
import { BucketObjectType } from "../types/BucketObjectType";
import { LegacyBucketFile, LegacyBucketObject, mapItemToLegacyBucketObject } from "./LegacyBucketObject";
import { LegacyBucketObjectItem, mapToLegacyBucketObjectItem } from "./LegacyBucketObjectItem";
import { useLegacyBucketExplorerFilters } from "./useLegacyBucketExplorerFilters";

interface LegacyBucketContentExplorerProps {
    bucketUniqueId: string;
    bucketContent: LegacyBucketObject[];
    isLoading: boolean;
    selection?: {
        selectMany?: {
            text: string;
            icon: string;
            onClick(selectedItems: LegacyBucketObject[]): void;
        };
        onChange?(selectedItems: LegacyBucketObject[]): void;
    };
    onFileClick?(file: LegacyBucketFile): void;
}

export const LegacyBucketContentExplorer: React.VFC<LegacyBucketContentExplorerProps> = ({
    bucketUniqueId,
    bucketContent,
    isLoading,
    selection,
    onFileClick
}: LegacyBucketContentExplorerProps) => {
    const {
        fileName,
        pathFolders,
        sort,
        openFolder,
        goBackToFolder,
        root,
        setSort,
        search,
        sortBucketContent,
        filterBucketContentByFolders,
        filterBucketContentByFileName
    } = useLegacyBucketExplorerFilters();

    const [allObjects, setAllObjects] = useState<LegacyBucketObjectItem[]>([]);
    const [currentObjects, setCurrentObjects] = useState<LegacyBucketObjectItem[]>([]);
    const [selectedObjects, setSelectedObjects] = useState<LegacyBucketObjectItem[]>([]);

    useEffect(() => {
        setAllObjects(bucketContent.map(mapToLegacyBucketObjectItem));
    }, [bucketContent, bucketContent.length]);

    useEffect(() => {
        let filteredObjects: LegacyBucketObjectItem[] = [];

        if (!fileName || fileName.length < 3) {
            filteredObjects = filterBucketContentByFolders(allObjects, pathFolders);
        } else {
            filteredObjects = filterBucketContentByFileName(allObjects, fileName);
        }

        const sortedContent = sortBucketContent(filteredObjects, sort);

        setCurrentObjects(sortedContent);
    }, [allObjects, sort, pathFolders, fileName]);

    const handleItemClicked = (item: LegacyBucketObjectItem) => {
        if (item.type === BucketObjectType.Folder) {
            openFolder(item.name);
        } else if (onFileClick) {
            onFileClick(item);
        }
    };

    const handleSelection = selection
        ? (selectedItems: LegacyBucketObjectItem[]) => {
              setSelectedObjects(selectedItems);

              if (selection.onChange) {
                  selection.onChange(selectedItems.map(mapItemToLegacyBucketObject));
              }
          }
        : undefined;

    const columns = createListTableColumns<LegacyBucketObjectItem>([
        {
            type: "interactive",
            key: "name",
            name: "Name",
            minWidth: 250,
            isSortable: true,
            onItemInvoked: handleItemClicked,
            onRender: renderObjectName
        },
        {
            type: "basic",
            key: "sizeInBytes",
            name: "File size",
            isResizable: false,
            isSortable: true,
            minWidth: 100,
            onRender: renderFileSize
        },
        {
            type: "basic",
            key: "type",
            name: "Type",
            isResizable: false,
            isSortable: true,
            minWidth: 100,
            onRender: renderType
        },
        {
            type: "basic",
            key: "lastModified",
            name: "Last modified",
            minWidth: 140,
            isResizable: false,
            isSortable: true,
            onRender: renderDateTime
        }
    ]);

    const explorerBreadcrumbs = (
        <Breadcrumb
            items={[
                { key: bucketUniqueId, text: bucketUniqueId, onClick: root },
                ...pathFolders.map<BreadcrumbItem>((folderName) => ({
                    key: folderName,
                    text: folderName,
                    onClick: () => goBackToFolder(folderName)
                }))
            ]}
            testId="explorer-breadcrumbs"
        />
    );

    const buttons = selection?.selectMany
        ? [
              {
                  text: selection.selectMany.text,
                  icon: selection.selectMany.icon,
                  onClick: () => selection.selectMany?.onClick(selectedObjects.map(mapItemToLegacyBucketObject)),
                  disabled: selectedObjects.length === 0,
                  render: SelectedManyActionButton,
                  testId: "explorer-actions-upload"
              } as ButtonRenderProps<SelectedManyActionButtonProps>
          ]
        : undefined;

    return (
        <ContentSection header={explorerBreadcrumbs} buttons={buttons}>
            <Stack tokens={{ maxWidth: 300 }} styles={{ root: { paddingLeft: 50 } }}>
                <SearchBox
                    placeholder="File name"
                    onChange={search}
                    value={fileName}
                    testId={"explorer-filters-name"}
                />
            </Stack>
            <ListTable
                columns={columns}
                items={currentObjects}
                isLoading={isLoading}
                noItemsMessage="This folder is empty"
                sort={sort}
                onSortChange={setSort}
                onSelectionChange={handleSelection}
                isItemSelectable={isItemSelectable}
                onRenderRow={renderExplorerRow}
            />
        </ContentSection>
    );
};

interface SelectedManyActionButtonProps {
    text: string;
    icon: string;
    disabled?: boolean;
    onClick(): void;
    testId?: string;
}

const SelectedManyActionButton: React.VFC<SelectedManyActionButtonProps> = ({
    text,
    icon,
    disabled,
    onClick,
    testId
}: SelectedManyActionButtonProps) => (
    <CommandBarButton
        text={text}
        icon={icon}
        onClick={onClick}
        testId={testId}
        styles={{ root: { width: text.length * 10 } }}
        disabled={disabled}
    />
);

const renderExplorerRow = (props?: DetailsRowProps, defaultRender?: RenderFunction<DetailsRowProps>) => {
    ensure(props);
    ensure(defaultRender);

    const testProps = {
        "data-testid": "explorer-row",
        "data-testkey": props.item.id,
        "data-testdisabled": props.item.isInactive,
        "data-testname": getFileName(props.item.name),
        "data-testext": props.item.type === BucketObjectType.File ? getFileExtension(props.item.name) : ""
    };

    const item = props.item as LegacyBucketObjectItem;

    const colors = isActiveObject(item)
        ? undefined
        : {
              backgroundColor: Theme.semanticColors.disabledBackground,
              color: Theme.semanticColors.disabledText
          };

    return defaultRender({
        ...props,
        ...testProps,
        id: item.id ? `explorer-item-${item.id}` : undefined,
        styles: {
            ...props.styles,
            root: {
                ...(props.styles ? props.styles["root"] : undefined),
                ...colors
            }
        }
    });
};

const renderObjectName = (
    item?: LegacyBucketObjectItem,
    _index?: number,
    column?: ListTableColumn<unknown> & IColumn
): ReactElement | null => {
    ensure(item);
    ensure(column);
    ensure(column.currentWidth);

    const maxCellWidth = column.currentWidth - 30;
    const iconSize = 18;

    const isActive = isActiveObject(item);

    const onClick = () => column.onItemInvoked && column.onItemInvoked(item);

    return (
        <Stack horizontal={true} verticalAlign="center" tokens={{ childrenGap: 15, maxWidth: maxCellWidth }}>
            <Icon size={iconSize} iconName={item.type === BucketObjectType.Folder ? "FabricFolder" : "Page"} />
            <Link
                disabled={!isActive}
                tooltipContent={getInactiveFileTooltip(item)}
                onClick={onClick}
                styles={{
                    root: { width: "100%" }
                }}
            >
                <Text
                    value={item.name}
                    maxLength={maxCellWidth / 10}
                    tooltip={getInactiveFileTooltip(item)}
                    variant="smallPlus"
                />
            </Link>
        </Stack>
    );
};

const renderFileSize = (item?: LegacyBucketObjectItem): ReactElement | null => {
    ensure(item);

    return <Stack>{formatBytes(item.sizeInBytes)}</Stack>;
};

const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) {
        return "0 Bytes";
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const renderType = (item?: LegacyBucketObjectItem): ReactElement | null => {
    ensure(item);

    if (item.type === BucketObjectType.Folder) {
        return <Stack>Folder</Stack>;
    }

    return <Stack>{getFileExtension(item.path)}</Stack>;
};

const renderDateTime = (item?: LegacyBucketObjectItem): ReactElement | null => {
    ensure(item);

    if (item.type === BucketObjectType.File) {
        return (
            <Stack>
                <DateTimeDisplay date={item.lastModified} />
            </Stack>
        );
    }

    return <Stack>-</Stack>;
};

const isItemSelectable = (item: LegacyBucketObjectItem): boolean => {
    if (item.isSelectable === undefined) {
        return true;
    }

    return typeof item.isSelectable === "string" ? item.isSelectable.length === 0 : item.isSelectable;
};

const isActiveObject = (item: LegacyBucketObjectItem): boolean => {
    if (item.type === BucketObjectType.File && item.isInactive === true) {
        return false;
    }

    return true;
};

const getInactiveFileTooltip = (item: LegacyBucketObjectItem): string | undefined => {
    if (item.type === BucketObjectType.File && !isActiveObject(item) && typeof item.isSelectable === "string") {
        return item.isSelectable;
    }

    return undefined;
};
